import "./CaseEditPopUp.css";

import { Field, FieldProps, Form, Formik } from "formik";

import { User } from "pwapi";

import { requestScreen, undoScreen } from "shared/actions/caseDetailActions";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { specimen_source } from "shared/utils/specimenUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import { useAppDispatch } from "store/hooks";

import { AdminFieldAssignment } from "../edit/fields";
import { UserOption } from "../edit/ResourceOptions";
import PopUpDialog from "../PopUpDialog";

const CaseEditPopUp = ({
  adminCase,
  onClose,
}: {
  adminCase: CaseAdminDetail;
  onClose: Function;
}) => {
  const dispatch = useAppDispatch();

  const cancelScreen = () => {
    adminCase.id &&
      dispatch(undoScreen({ id: adminCase.id })).then(() => {
        onClose();
      });
  };

  return adminCase.is_screening ? (
    <PopUpDialog onClose={() => onClose()} title="Undo Screen" type="warning">
      <div className="CaseWarningDialog">
        <span>
          Are you sure you want to undo the screen?
          <br />
          <br />
          The case's active assignment will be{" "}
          {[
            adminCase.finalize_assignment?.first_name,
            adminCase.finalize_assignment?.last_name,
          ]
            .filter((s) => s)
            .join(" ")}{" "}
          after undoing the screen.
        </span>
        <div className="actions__container">
          <PathologyWatchButton onClick={() => onClose()}>
            Cancel
          </PathologyWatchButton>
          <PathologyWatchButton color="red" onClick={cancelScreen}>
            Undo Screen
          </PathologyWatchButton>
        </div>
      </div>
    </PopUpDialog>
  ) : (
    <PopUpDialog onClose={() => onClose()} title="Setup Screen" type="default">
      <Formik
        initialValues={{
          screen_assignment: adminCase.screen_assignment,
          specimens: (adminCase.specimens?.map((spec) => spec.id) ||
            []) as number[],
        }}
        onSubmit={(values) => {
          if (values.specimens.length && adminCase.active_assignment) {
            dispatch(
              requestScreen({
                id: adminCase.id || 0,
                requestBody: {
                  //@ts-ignore
                  screen_assignment:
                    values.screen_assignment &&
                    values.screen_assignment?.id !==
                      adminCase.screen_assignment?.id
                      ? values.screen_assignment?.id
                      : undefined,
                  selected_specimens: values.specimens,
                },
              })
            ).then(() => onClose());
          }
        }}
      >
        {({ setFieldValue, values }: any) => {
          return (
            <Form className="CaseWarningDialog" style={{ padding: "0" }}>
              <div className="RequestScreen__container">
                <div className="splitContainer">
                  <CustomInput label="Finalize Assignment">
                    {adminCase.active_assignment?.id ? (
                      <UserOption object={adminCase.active_assignment} />
                    ) : (
                      <>
                        Missing an active assignment to assign as the finalize
                        assignment
                      </>
                    )}
                  </CustomInput>
                  <Field name="screen_assignment">
                    {({ form, field }: FieldProps) => (
                      <AdminFieldAssignment
                        {...field}
                        field="screen_assignment"
                        label="Screen Assignment"
                        onChange={(newValue: { object: User }) => {
                          form.setFieldValue(
                            "screen_assignment",
                            newValue.object
                          );
                        }}
                      />
                    )}
                  </Field>
                </div>
                <div className="splitContainer">
                  <span>Select Specimens to Screen</span>
                  {adminCase.specimens.map((spec) => (
                    <div className="panelContainer__inputGroup" key={spec.id}>
                      <input
                        type="checkbox"
                        id={`specimen-${spec.id}`}
                        checked={values.specimens.includes(spec.id) || false}
                        onChange={() => {
                          spec.id &&
                            setFieldValue(
                              "specimens",
                              values.specimens.includes(spec.id)
                                ? values.specimens.filter(
                                    (id: number) => id !== spec.id
                                  )
                                : [...values.specimens, spec.id]
                            );
                        }}
                      />
                      <label htmlFor={`specimen-${spec.id}`}>
                        <strong>{spec.name} - </strong>
                        <span>{specimen_source(spec, adminCase)}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className="actions__container"
                style={{
                  borderTop: "2px solid var(--green)",
                  marginTop: "0",
                  padding: "1em 2em 2em 2em",
                }}
              >
                {(!values.specimens.length || !adminCase.active_assignment) && (
                  <div className="RequestScreen__errorContainer">
                    {!values.specimens.length && (
                      <span>Must select at least one specimen.</span>
                    )}
                    {!adminCase.active_assignment && (
                      <span>Case has no active assignment.</span>
                    )}
                  </div>
                )}
                <PathologyWatchButton onClick={() => onClose()}>
                  Cancel
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  color="green"
                  disabled={
                    !values.specimens.length || !adminCase.active_assignment
                  }
                >
                  Set Case To Screen
                </PathologyWatchButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </PopUpDialog>
  );
};

export default CaseEditPopUp;
